/**
 * Wireline & Slickline WL-2 helpers (fieldiq-api#1338): who may press what on
 * jobs and day sheets, the status skins, the warnings as sentences, and the
 * field surface's one camelize.
 *
 * Copied in shape from lib/well-servicing.ts, never imported from it: each
 * vertical owns its own helpers (cross-vertical isolation).
 *
 * NOTHING HERE COMPUTES A FIGURE. Hours, footage, derived misfires and depths
 * in the tenant unit are the API's; this file turns them into words. A WARNING
 * NEVER DISABLES ANYTHING: the helpers that read warnings return text, and the
 * one refusal the rig-up rule can raise (409 rig_up_test_required) is read
 * apart from the warning of the same rule.
 */
import type { WlSkin } from './wireline-slickline';
import { wlApiError, WL_PERMISSION_REFUSAL } from './wireline-slickline';
import { formatCalendarDate } from './calendar-date';
import { fmtQty } from './display-labels';
import type { WlQty, WlWarningEntry } from '../types/wireline-slickline-jobs';

// ─── Who may press what (mirrors fieldiq-api lib/wl-roles.ts) ────────────────
//
// For hiding a button, not for enforcement: a 403 that still arrives is shown.

export const WL_DISPATCH_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher'] as const;
export const WL_RENT_IN_READ_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher', 'logistics'] as const;
export const WL_SHEET_WRITE_ROLES = ['admin', 'office_staff', 'supervisor', 'field_worker'] as const;
export const WL_SHEET_APPROVE_ROLES = ['admin', 'office_staff'] as const;
/** The crew positions that may record runs, pressure tests and drills on a sheet (lib/wl-sheet-links.ts). */
export const WL_RECORDER_POSITIONS = ['supervisor', 'operator'] as const;

const inList = (list: readonly string[]) => (role: string | null | undefined) => !!role && list.includes(role);
/** Job create / edit / status / delete, units and rent-ins on a job, the pressure stack. */
export const canDispatchWl = inList(WL_DISPATCH_ROLES);
/** Rent-ins are read by dispatch and logistics only. */
export const canReadWlRentIns = inList(WL_RENT_IN_READ_ROLES);
/** Open, edit and submit a day sheet; record runs. */
export const canWriteWlSheets = inList(WL_SHEET_WRITE_ROLES);
/** Approve, return and delete a day sheet. */
export const canApproveWlSheets = inList(WL_SHEET_APPROVE_ROLES);

/**
 * May THIS user record on THIS sheet from the field? A draft, not locked, and
 * the user on its crew as supervisor or operator. A helper sees it read only.
 * The API decides (403 position_cannot_record); this hides the buttons.
 */
export function wlCanRecordOnSheet(
  sheet: { status: string; locked?: boolean | null; crew: Array<{ userId: string; position: string }> } | null | undefined,
  userId: string | null | undefined,
): boolean {
  if (!sheet || !userId || sheet.status !== 'draft' || sheet.locked) return false;
  return sheet.crew.some((c) => c.userId === userId && (WL_RECORDER_POSITIONS as readonly string[]).includes(c.position));
}

/** The caller's position on the sheet's crew, or null. */
export const wlSheetPositionOf = (sheet: { crew: Array<{ userId: string; position: string }> } | null | undefined, userId: string | null | undefined) =>
  (userId && sheet?.crew.find((c) => c.userId === userId)?.position) || null;

// ─── Refusals ────────────────────────────────────────────────────────────────

/**
 * A WL-2 write's error as a sentence. Every coded refusal carries the API's
 * sentence. A 403 with a code (position_cannot_record, position_cannot_edit_crew,
 * not_on_job) names who can act, so it is shown as sent; a bare 403 is the
 * permission line.
 */
export function wlJobRefusal(err: unknown, fallback: string): string {
  const e = wlApiError(err);
  if (e.status === 403) return e.code && e.message ? e.message : WL_PERMISSION_REFUSAL;
  if (e.status === 404 && !e.code) return 'That record no longer exists. It may have been deleted.';
  return e.message ?? fallback;
}

/** 409 rig_up_test_required: the setting is on and no passing rig-up test is on the unit's day. A REFUSAL. */
export function wlRigUpRefusal(err: unknown): string | null {
  const e = wlApiError(err);
  if (e.status !== 409 || e.code !== 'rig_up_test_required') return null;
  return e.message ?? "Record a passing rig-up pressure test on this unit's day sheet before its first run of the day";
}

/** 409 pressure_stack_refused: the guard's sentence and each failure's own line. */
export function wlStackRefusal(err: unknown): { message: string; failures: string[] } | null {
  const e = wlApiError(err);
  if (e.status !== 409 || e.code !== 'pressure_stack_refused') return null;
  const raw = Array.isArray(e.extra.failures) ? (e.extra.failures as Array<{ error?: unknown }>) : [];
  return {
    message: e.message ?? 'The pressure stack cannot go on this well.',
    failures: raw.map((f) => (typeof f?.error === 'string' ? f.error : null)).filter((x): x is string => !!x),
  };
}

/** 409 sheet_exists: the sheet already open for that unit and day. */
export function wlExistingSheetId(err: unknown): string | null {
  const e = wlApiError(err);
  return e.status === 409 && e.code === 'sheet_exists' && typeof e.extra.existingId === 'string' ? e.extra.existingId : null;
}

// ─── Warnings as sentences ───────────────────────────────────────────────────

const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * One API warning as a sentence. The numbers in it are the API's (a misfire
 * warning carries the count entered AND loaded − fired, both computed there).
 * An unknown code shows its own message, else the code: never dropped.
 */
export function wlWarningText(w: WlWarningEntry, opts: { unitName?: (id: string) => string | null } = {}): string {
  const run = n(w.runNumber) !== null ? `Run ${w.runNumber}: ` : '';
  switch (w.code) {
    case 'rig_up_test_missing':
      return w.required === true
        ? 'No passing rig-up pressure test is on this unit’s day sheet. A rig-up test is required before the first run.'
        : 'No passing rig-up pressure test is on this unit’s day sheet. Record one before the first run of the day.';
    case 'misfire_count_mismatch':
      // A sentence on its own or after "Run N: " — either way it starts with a capital.
      return `${run}Gun ${w.gunPosition ?? '—'} has ${w.misfireCount ?? '—'} misfire${w.misfireCount === 1 ? '' : 's'} entered, but loaded minus fired is ${w.derivedMisfires ?? '—'}. Check the count.`;
    case 'footage_mismatch':
      return `${run}Footage submitted ${fmtQty(n(w.submittedM), 2)} m differs from the computed ${fmtQty(n(w.computedM), 2)} m.`;
    case 'meter_below_previous': {
      const name = typeof w.equipmentId === 'string' ? opts.unitName?.(w.equipmentId) ?? null : null;
      const when = typeof w.previousDate === 'string' ? ` on ${wlDay(w.previousDate)}` : '';
      return `${name ?? 'A unit'}: hour meter ${fmtQty(n(w.entered), 2)} is below the previous reading ${fmtQty(n(w.previous), 2)}${when}.`;
    }
    case 'due_soon':
      return typeof w.message === 'string' && w.message ? w.message : 'A certificate is due soon.';
    // WL-3 (api#1339): the service report's warnings. Shown, never blocking.
    case 'sheets_not_approved': {
      const c = n(w.count);
      return c === null
        ? 'Some day sheets on this job are not approved by the office yet. They are not in the report.'
        : `${c} day sheet${c === 1 ? ' is' : 's are'} not approved by the office yet. ${c === 1 ? 'It is' : 'They are'} not in the report.`;
    }
    case 'no_approved_sheets':
      return 'No day sheet on this job is approved by the office yet, so the report has no runs in it.';
    case 'source_custody_incomplete': {
      const serial = typeof w.serialNumber === 'string' && w.serialNumber ? ` ${w.serialNumber}` : '';
      return `${run}Source${serial} does not have all four custody times recorded.`;
    }
    case 'content_changed_since_refresh':
      return 'The work recorded on this job changed after the draft was last refreshed. The issued report holds the work as it stands now.';
    default:
      return typeof w.message === 'string' && w.message ? w.message : w.code;
  }
}

// ─── Display ─────────────────────────────────────────────────────────────────

export const wlDay = (value: string | null | undefined): string => formatCalendarDate(value) ?? '—';

/** Hours as the API computed them; absent → `—`. A real 0 is `0 h`. */
export function wlHours(v: number | null | undefined): string {
  const s = fmtQty(v, 2);
  return s === '—' ? s : `${s} h`;
}

/** `{ value, unit }` as sent; absent → `—`. Never converted here. */
export function wlQty(q: WlQty | null | undefined, digits = 2): string {
  if (!q) return '—';
  const s = fmtQty(q.value, digits);
  return s === '—' ? s : `${s} ${q.unit}`;
}

/** A clock pair from the API; either absent → `—`. */
export const wlClockRange = (a: string | null | undefined, b: string | null | undefined) => (a && b ? `${a}–${b}` : '—');

/** The instant to start a test or drill at: now if inside the sheet, else the sheet's start. */
export function wlDefaultInstant(startAt: string | null | undefined, endAt: string | null | undefined, now: Date = new Date()): string {
  const s = startAt ? Date.parse(startAt) : NaN;
  const e = endAt ? Date.parse(endAt) : NaN;
  const t = now.getTime();
  if (Number.isFinite(s) && Number.isFinite(e) && (t < s || t > e)) return new Date(s).toISOString();
  return now.toISOString();
}

const UNKNOWN_SKIN: WlSkin = { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };
const pick = (map: Record<string, WlSkin>, v: string | null | undefined, apiLabel?: string | null): WlSkin => {
  const base = v && Object.prototype.hasOwnProperty.call(map, v) ? map[v] : v ? { ...UNKNOWN_SKIN, label: v } : UNKNOWN_SKIN;
  return apiLabel ? { ...base, label: apiLabel } : base;
};

/** Draft steel, scheduled blue, in progress amber, completed green, cancelled red. */
export const WL_JOB_STATUS_SKIN: Record<string, WlSkin> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  scheduled: { label: 'Scheduled', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  in_progress: { label: 'In progress', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  completed: { label: 'Completed', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  cancelled: { label: 'Cancelled', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};
export const wlJobStatusSkin = (status: string | null | undefined, apiLabel?: string | null) => pick(WL_JOB_STATUS_SKIN, status, apiLabel);

/** Draft steel, submitted blue (waiting on the office), approved green. */
export const WL_SHEET_STATUS_SKIN: Record<string, WlSkin> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  submitted: { label: 'Submitted', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  office_approved: { label: 'Approved by office', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
};
export const wlSheetStatusSkin = (status: string | null | undefined, apiLabel?: string | null) => pick(WL_SHEET_STATUS_SKIN, status, apiLabel);

export const WL_RENT_STATUS_SKIN: Record<string, WlSkin> = {
  upcoming: { label: 'Upcoming', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  on_rent: { label: 'On rent', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  off_rent: { label: 'Off rent', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
export const wlRentStatusSkin = (status: string | null | undefined) => pick(WL_RENT_STATUS_SKIN, status);

/** Labels for a status move the API allows (`allowedTransitions`). The API decides which appear. */
export const WL_TRANSITION_LABEL: Record<string, string> = {
  draft: 'Back to draft',
  scheduled: 'Schedule',
  in_progress: 'Start',
  completed: 'Complete',
  cancelled: 'Cancel job',
};

/**
 * Which detail sections a run kind shows by default. A display choice: the API
 * accepts every set on every kind, and a section that already holds rows is
 * always shown.
 */
export const WL_RUN_KIND_SECTIONS: Record<string, Array<'stops' | 'plugs' | 'logs' | 'guns'>> = {
  gauge_drift: [],
  bhp_survey: ['stops'],
  fishing: [],
  plug_set: ['plugs'],
  plug_pull: ['plugs'],
  valve_set: ['plugs'],
  valve_pull: ['plugs'],
  bailing: [],
  logging: ['logs'],
  perforating: ['guns'],
  setting_tool: ['plugs'],
  other: [],
};

// ─── The field surface's one camelize ────────────────────────────────────────

const toCamel = (k: string) => k.replace(/_([a-z0-9])/g, (_m, c: string) => c.toUpperCase());

/**
 * The field surface (/field/*) answers snake_case. The field hooks camelize the
 * body ONCE, here, so the field screens read the same shapes as the office
 * ones — one reader per field, never a both-ways `a ?? b` at each call site.
 * Keys only; values (status codes like `office_approved`) are untouched.
 */
export function wlCamelKeys<T = unknown>(v: unknown): T {
  if (Array.isArray(v)) return v.map((x) => wlCamelKeys(x)) as unknown as T;
  if (v && typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype) {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) out[toCamel(k)] = wlCamelKeys(val);
    return out as T;
  }
  return v as T;
}
